import "./ServicesTable.scss";
// -- Packages
import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react";
// -- Prime React
import { Panel } from "primereact/panel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Badge } from "primereact/badge";
// -- Local
import ServiceSearchStore from "../ServiceSearchStore";
import { usePanelDialog } from "../../../hooks/UsePanelDialog";
import { Service } from "../../../models/Service";
import { DetailDialog } from "./DetailDialog";
import PortalService from "../../../services/PortalService";

type Props = {};

export const ServicesTable: React.FC<Props> = observer(() => {
    const serviceDialog = usePanelDialog<Service>();
    const tablePanelRef = useRef<HTMLDivElement | null>(null);

    const handleRowClicked = async (id: number): Promise<void> => {
        await PortalService.GetServiceByIdAsync(id).then((res) =>
            serviceDialog.openDialog(res)
        );
    };

    const statusBodyTemplate = (rowData: Service) => {
        var severityCode =
            rowData.serviceStatus === "Closed" ? "danger" : "success";

        return (
            <React.Fragment>
                <Badge
                    value={rowData.serviceStatus}
                    size="large"
                    severity={severityCode}
                />
            </React.Fragment>
        );
    };

    const activateSortableHeader = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        const headerCell = event.currentTarget.closest("th");

        if (headerCell instanceof HTMLElement) {
            headerCell.click();
        }
    };

    const renderSortableHeader = (label: string) => (
        <button
            type="button"
            aria-label={`Sort by ${label}`}
            className="sortable-column-label"
            onClick={activateSortableHeader}
        >
            {label}
        </button>
    );

    useEffect(() => {
        const root = tablePanelRef.current;
        if (!root) return;

        const setLabel = (selector: string, label: string) => {
            root.querySelectorAll(selector).forEach((element) => {
                element.setAttribute("aria-label", label);
                element.setAttribute("title", label);
            });
        };

        const applyAccessibilityFixes = () => {
            const currentRoot = tablePanelRef.current;
            if (!currentRoot) return;

            setLabel(".p-paginator-first", "First page");
            setLabel(".p-paginator-prev", "Previous page");
            setLabel(".p-paginator-next", "Next page");
            setLabel(".p-paginator-last", "Last page");

            currentRoot.querySelectorAll(".p-paginator-page").forEach((element) => {
                const pageText = element.textContent?.trim();
                if (pageText) {
                    const label = `Page ${pageText}`;
                    element.setAttribute("aria-label", label);
                    element.setAttribute("title", label);
                }
            });

            const paginator = currentRoot.querySelector(".p-paginator") as HTMLElement | null;
            if (paginator) {
                const dropdowns = paginator.querySelectorAll(".p-dropdown");
                const rowsPerPageDropdown =
                    dropdowns.length > 0
                        ? (dropdowns[dropdowns.length - 1] as HTMLElement)
                        : null;

                if (rowsPerPageDropdown) {
                    const hiddenInput = rowsPerPageDropdown.querySelector(
                        ".p-hidden-accessible input"
                    ) as HTMLInputElement | null;

                    if (hiddenInput) {
                        const inputId = "rows-per-page-input";
                        hiddenInput.id = inputId;

                        let injectedLabel = paginator.querySelector(
                            "#rows-per-page-label"
                        ) as HTMLLabelElement | null;

                        if (!injectedLabel) {
                            injectedLabel = document.createElement("label");
                            injectedLabel.id = "rows-per-page-label";
                            injectedLabel.className = "rows-per-page-label";
                            injectedLabel.htmlFor = inputId;
                            injectedLabel.textContent = "Show Entries";

                            rowsPerPageDropdown.insertAdjacentElement(
                                "beforebegin",
                                injectedLabel
                            );
                        } else {
                            injectedLabel.htmlFor = inputId;
                        }

                        hiddenInput.setAttribute("aria-labelledby", "rows-per-page-label");
                        rowsPerPageDropdown.setAttribute(
                            "aria-labelledby",
                            "rows-per-page-label"
                        );
                    }

                    const trigger = rowsPerPageDropdown.querySelector(
                        ".p-dropdown-trigger"
                    ) as HTMLElement | null;

                    if (trigger) {
                        trigger.setAttribute("title", "Show Entries");
                    }
                }
            }

            currentRoot.querySelectorAll(".p-sortable-column").forEach((element) => {
                const header = element as HTMLElement;
                const text = header.textContent?.trim();

                if (text) {
                    const label = `Sort by ${text}`;
                    header.setAttribute("aria-label", label);
                    header.setAttribute("title", label);
                }

                header.removeAttribute("tabindex");
            });

            const tableRows = currentRoot.querySelectorAll(".p-datatable-tbody > tr");

            tableRows.forEach((row) => {
                const htmlRow = row as HTMLTableRowElement;

                htmlRow.setAttribute("tabindex", "0");
                htmlRow.setAttribute("role", "button");
                htmlRow.setAttribute("aria-label", "Open service details");

                if (!(htmlRow as any).__rowKeyHandler) {
                    const keyHandler = async (event: Event) => {
                        const e = event as KeyboardEvent;

                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();

                            const rows = currentRoot.querySelectorAll(".p-datatable-tbody > tr");
                            const rowIndex = Array.from(rows).indexOf(htmlRow);
                            const rowData = ServiceSearchStore.searchResults[rowIndex];

                            if (rowData?.id) {
                                await handleRowClicked(rowData.id);
                            }
                        }
                    };

                    htmlRow.addEventListener("keydown", keyHandler);
                    (htmlRow as any).__rowKeyHandler = keyHandler;
                }
            });
        };

        const rafId = window.requestAnimationFrame(() => {
            applyAccessibilityFixes();
        });

        const observer = new MutationObserver(() => {
            applyAccessibilityFixes();
        });

        observer.observe(root, {
            childList: true,
            subtree: true
        });

        return () => {
            window.cancelAnimationFrame(rafId);
            observer.disconnect();

            root.querySelectorAll(".p-datatable-tbody > tr").forEach((row) => {
                const htmlRow = row as HTMLTableRowElement;
                const keyHandler = (htmlRow as any).__rowKeyHandler;
                if (keyHandler) {
                    htmlRow.removeEventListener("keydown", keyHandler);
                }
            });
        };
    }, [ServiceSearchStore.searchResults, ServiceSearchStore.isLoading]);

    return (
        <>
            <section aria-labelledby="results-heading">
                <h2 id="results-heading" className="visually-hidden">
                    Search Results
                </h2>
                {ServiceSearchStore.initialSearch && (
                    <div ref={tablePanelRef}>
                        <Panel id="service-search-table" className="p-panel" header=" ">
                            <br />
                            <caption id="results-heading" className="table-caption">
                                Services
                            </caption>
                            <hr />
                            {ServiceSearchStore.isLoading && (
                                <ProgressSpinner strokeWidth="5" animationDuration="1s" />
                            )}
                            {!ServiceSearchStore.isLoading && (
                                <DataTable
                                    value={ServiceSearchStore.searchResults}
                                    autoLayout
                                    onRowClick={(e) => handleRowClicked(e.data.id)}
                                    rowHover
                                    paginator
                                    rows={10}
                                    emptyMessage="No services found for provided criteria"
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                    rowsPerPageOptions={[10, 25, 50]}
                                >
                                    <Column
                                        header={renderSortableHeader("Case Number")}
                                        className="min-width-175 pointer"
                                        sortable
                                        field="caseNumber"
                                    />
                                    <Column
                                        header={renderSortableHeader("File Number")}
                                        sortable
                                        field="fileNumber"
                                    />
                                    <Column
                                        header={renderSortableHeader("Paper Type")}
                                        className="pointer"
                                        sortable
                                        field="requestCategory"
                                    />
                                    <Column
                                        header={renderSortableHeader("Manner of Service")}
                                        className="pointer"
                                        sortable
                                        field="manner"
                                    />
                                    <Column
                                        header={renderSortableHeader("Party Served")}
                                        className="pointer"
                                        sortable
                                        field="partyServedTo"
                                    />
                                    <Column
                                        header={renderSortableHeader("Person Served")}
                                        className="pointer"
                                        sortable
                                        field="personServedTo"
                                    />
                                    <Column
                                        header={renderSortableHeader("Service Status")}
                                        className="pointer"
                                        sortable
                                        field="serviceStatus"
                                        body={statusBodyTemplate}
                                    />
                                </DataTable>
                            )}
                        </Panel>
                    </div>
                )}
            </section>
            <DetailDialog data={serviceDialog} />
        </>
    );
});